import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, IconButton, Tooltip } from '@mui/material';
import { Box, Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import regexpPatterns from '@/shared/constants/regexpPatterns';
import User from '@/shared/types/user';

type UpdateUserDialogProps = {
  user: User;
  errors?: string[];
  onClose: () => void;
  onProceedUpdateUser: (user: User) => void;
};

const UpdateUserDialog: FC<UpdateUserDialogProps> = ({
  user,
  errors,
  onClose,
  onProceedUpdateUser,
}) => {
  const { t } = useTranslation(['usersManagement', 'common']);

  const [form, setForm] = useState<Partial<User>>({
    first_name: user.first_name,
    last_name: user.last_name,
    phone_number: user.phone_number,
    enabled: user.enabled,
  });

  const [formErrors, setFormErrors] = useState<Partial<User>>();

  const onValidate = () => {
    const errors: Partial<User> = {};

    if (!form.first_name) {
      errors.first_name = t('usersManagement:first_name_required');
    }

    if (!form.phone_number) {
      errors.phone_number = t('usersManagement:phone_number_required');
    } else if (!regexpPatterns.phoneNumberValidator.test(form.phone_number)) {
      errors.phone_number = t('usersManagement:phone_number_invalid');
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  if (errors?.length) {
    return (
      <ModalNotification
        size='sm'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('usersManagement:updateUser'),
          content: (
            <Stack spacing={2} useFlexGap>
              {t('bookingsServices:removeContactsError')}
              {errors.map((error) => (
                <Alert key={error} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('usersManagement:updateUser'),
        content: (
          <Stack sx={{ py: 1 }}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'first_name',
                    label: t('usersManagement:first_name'),
                    value: form.first_name,
                    placeholder: t('usersManagement:first_name'),
                    error: !!formErrors?.first_name,
                    helperText: formErrors?.first_name,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'last_name',
                    label: t('usersManagement:last_name'),
                    value: form.last_name,
                    placeholder: t('usersManagement:last_name'),
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'phone_number',
                    label: t('usersManagement:phone_number'),
                    value: form.phone_number,
                    placeholder: t('usersManagement:phone_number'),
                    error: !!formErrors?.phone_number,
                    helperText: formErrors?.phone_number,
                  },
                },
                {
                  columnRenderer(field) {
                    return (
                      <Stack
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr',
                          alignItems: 'center',
                        }}
                      >
                        {field}

                        <Box>
                          <Tooltip
                            title={t('usersManagement:accountEnabledHint')}
                            placement='top'
                            tabIndex={-1}
                            arrow
                            PopperProps={{
                              sx: { maxWidth: 250 },
                            }}
                          >
                            <IconButton>
                              <InfoOutlinedIcon
                                sx={{ width: 16, height: 16 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Stack>
                    );
                  },
                  field_type: FieldType.Switch,
                  field_props: {
                    name: 'enabled',
                    label: t('usersManagement:accountEnabled'),
                    checked: form.enabled,
                  },
                },
              ]}
              getFieldsValues={(value) => {
                setForm((prev) => ({
                  ...prev,
                  ...value,
                }));
              }}
            />
          </Stack>
        ),
        actions: [
          {
            label: t('common:close'),
            onClick: onClose,
          },
          {
            label: t('usersManagement:updateUser'),
            variant: 'contained',
            onClick: () => {
              const isFormValid = onValidate();

              if (!isFormValid) return;

              onProceedUpdateUser({
                ...user,
                ...form,
              });
            },
          },
        ],
      }}
    />
  );
};

export default UpdateUserDialog;
